import React, { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Mail, Clock, Send } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  COMPANY,
  SERVICE_OPTIONS,
  buildWhatsAppLink,
} from "@/data/site";
import { WhatsAppIcon } from "@/components/WhatsAppFloat";

const InfoRow = ({ icon: Icon, label, value, href }) => (
  <div className="flex items-start gap-4">
    <span className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-[var(--kilic-blue)]/10 text-[var(--kilic-blue)]">
      <Icon className="h-5 w-5" />
    </span>
    <div>
      <div className="text-xs font-semibold uppercase tracking-wider text-neutral-400">
        {label}
      </div>
      {href ? (
        <a href={href} className="font-semibold text-neutral-900 hover:text-[var(--kilic-blue)]">
          {value}
        </a>
      ) : (
        <div className="font-semibold text-neutral-900">{value}</div>
      )}
    </div>
  </div>
);

export const Contact = () => {
  const [name, setName] = useState("");
  const [service, setService] = useState("");
  const [message, setMessage] = useState("");

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim() || !service) {
      toast.error("Bitte Namen und Dienstleistung angeben.");
      return;
    }
    const link = buildWhatsAppLink({ name, service, message });
    window.open(link, "_blank", "noopener,noreferrer");
    toast.success("WhatsApp wird geöffnet – wir melden uns schnellstmöglich!");
  };

  return (
    <section id="kontakt" className="relative bg-[#fafafa] py-24 md:py-32" data-testid="contact-section">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <div className="grid gap-12 lg:grid-cols-2">
          {/* Left: info */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-[var(--kilic-green-dark)]">
              Kontakt
            </span>
            <h2 className="mt-3 font-display text-3xl font-extrabold leading-tight tracking-tighter text-neutral-900 sm:text-4xl md:text-5xl">
              Jetzt Angebot{" "}
              <span className="text-gradient">per WhatsApp</span> anfragen
            </h2>
            <p className="mt-5 max-w-md text-sm text-neutral-600 sm:text-base md:text-lg">
              Füllen Sie das Formular aus – Ihre Anfrage wird direkt als
              WhatsApp-Nachricht vorbereitet. Schnell, unkompliziert und
              unverbindlich.
            </p>

            <div className="mt-10 space-y-6">
              <InfoRow
                icon={MapPin}
                label="Adresse"
                value={`${COMPANY.street}, ${COMPANY.city}`}
              />
              <InfoRow
                icon={Phone}
                label="Telefon"
                value={COMPANY.phoneDisplay}
                href={`tel:${COMPANY.phoneTel}`}
              />
              <InfoRow
                icon={Mail}
                label="E-Mail"
                value={COMPANY.email}
                href={`mailto:${COMPANY.email}`}
              />
              <InfoRow
                icon={Clock}
                label="Erreichbarkeit"
                value="Mo. – Sa. · 08:00 – 18:00 Uhr"
              />
            </div>

            <div className="mt-10 overflow-hidden rounded-3xl border border-black/5 shadow-sm">
              <iframe
                title="Standort Kilic Gebäudeservice"
                src="https://www.google.com/maps?q=Wilhelm-Wolters-Str.%20170,%2028309%20Bremen&output=embed"
                width="100%"
                height="240"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </motion.div>

          {/* Right: form */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="rounded-[2rem] border border-black/5 bg-white p-7 shadow-xl md:p-9"
          >
            <div className="mb-6 flex items-center gap-3">
              <span className="grid h-12 w-12 place-items-center rounded-2xl bg-[var(--kilic-green)] text-white">
                <WhatsAppIcon className="h-6 w-6" />
              </span>
              <div>
                <div className="font-display text-lg font-extrabold text-neutral-900">
                  Anfrage senden
                </div>
                <div className="text-sm text-neutral-500">
                  Antwort in der Regel innerhalb von 24h
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5" data-testid="whatsapp-quote-form">
              <div>
                <label className="mb-1.5 block text-sm font-semibold text-neutral-700">
                  Name & Vorname
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Max Mustermann"
                  data-testid="quote-name-input"
                  className="w-full rounded-xl border border-neutral-200 bg-neutral-50 px-4 py-3 text-sm outline-none transition-colors focus:border-[var(--kilic-blue)] focus:bg-white focus:ring-2 focus:ring-[var(--kilic-blue)]/15"
                />
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-semibold text-neutral-700">
                  Dienstleistung
                </label>
                <Select value={service} onValueChange={setService}>
                  <SelectTrigger
                    data-testid="quote-service-select"
                    className="h-12 rounded-xl border-neutral-200 bg-neutral-50 px-4 text-sm"
                  >
                    <SelectValue placeholder="Bitte wählen Sie aus …" />
                  </SelectTrigger>
                  <SelectContent>
                    {SERVICE_OPTIONS.map((opt) => (
                      <SelectItem key={opt} value={opt} data-testid={`quote-service-${opt}`}>
                        {opt}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-semibold text-neutral-700">
                  Ihre Nachricht
                </label>
                <textarea
                  rows={4}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Beschreiben Sie kurz Ihr Anliegen …"
                  data-testid="quote-message-input"
                  className="w-full resize-none rounded-xl border border-neutral-200 bg-neutral-50 px-4 py-3 text-sm outline-none transition-colors focus:border-[var(--kilic-blue)] focus:bg-white focus:ring-2 focus:ring-[var(--kilic-blue)]/15"
                />
              </div>

              <button
                type="submit"
                data-testid="quote-submit-btn"
                className="group flex w-full items-center justify-center gap-3 rounded-xl bg-[var(--kilic-green)] px-6 py-4 font-bold text-white shadow-lg shadow-green-600/25 transition-transform hover:scale-[1.02] active:scale-95"
              >
                <WhatsAppIcon className="h-5 w-5" />
                Per WhatsApp senden
                <Send className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </button>
              <p className="text-center text-xs text-neutral-400">
                Mit dem Absenden öffnet sich WhatsApp mit Ihrer vorausgefüllten
                Nachricht.
              </p>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
